import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import ConfirmationModal from "../../components/ConfirmationModal";
import AgentSessionList from "../../components/agent/AgentSessionList";
import AgentMessageList from "../../components/agent/AgentMessageList";
import { useAuth } from "../../providers/AuthContext";
import {
    deleteAgentSession,
    getAgentSession,
    listAgentSessions,
    streamAgentQuery,
} from "../../api/Agent";
import {
    applyTextEvent,
    firstUserMessageText,
    mergeArtifactsIntoMessage,
    mergeToolsIntoMessage,
    messagesFromSession,
    normalizeSessions,
    sessionIdOf,
    sortSessionsByUpdatedAt,
} from "../../helpers/agentEvents";

const STREAM_TIMEOUT_MS = 180000;
const COLD_START_MS = 4000;
const HEADER_PX = 70;

function isDesktopComposer() {
    return window.matchMedia("(min-width: 768px)").matches;
}

function isCoarsePointer() {
    return window.matchMedia("(pointer: coarse)").matches;
}

function storageKey(user) {
    return `agge.sessionId.${user?.localAccountId || user?.username || "user"}`;
}

function titlesKey(user) {
    return `agge.sessionTitles.${user?.localAccountId || user?.username || "user"}`;
}

function readTitleMap(user) {
    try {
        const parsed = JSON.parse(localStorage.getItem(titlesKey(user)) || "{}");
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

function unwrapEvent(event) {
    if (!event || typeof event !== "object") {
        return event;
    }
    if (event.hub_session_id || event.hubSessionId) {
        return event;
    }
    if (event.content || event.actions || event.partial === true || event.partial === false) {
        return event;
    }
    if (event.output && typeof event.output === "object") {
        return event.output;
    }
    return event;
}

function hubSessionIdOf(event) {
    return event?.hub_session_id || event?.hubSessionId || "";
}

const AgentPage = () => {
    const { t } = useTranslation();
    const { getAccessToken, user } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [sessionId, setSessionId] = useState("");
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [showColdStart, setShowColdStart] = useState(false);
    const [listOpen, setListOpen] = useState(false);
    const [sessionsLoading, setSessionsLoading] = useState(true);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [confirmState, setConfirmState] = useState({ isOpen: false, title: "", confirm: () => {} });
    const [titleMap, setTitleMap] = useState({});
    const abortRef = useRef(null);
    const coldStartRef = useRef(null);
    const gotEventRef = useRef(false);
    const sessionIdRef = useRef("");
    const streamGenRef = useRef(0);
    const composerRef = useRef(null);
    const titleMapRef = useRef({});
    const shellRef = useRef(null);

    const rememberTitle = useCallback(
        (id, rawTitle) => {
            const title = String(rawTitle || "").trim();
            if (!id || !title) {
                return;
            }
            const nextTitle = title.length > 48 ? `${title.slice(0, 47)}…` : title;
            if (titleMapRef.current[id] === nextTitle) {
                return;
            }
            const next = { ...titleMapRef.current, [id]: nextTitle };
            titleMapRef.current = next;
            setTitleMap(next);
            localStorage.setItem(titlesKey(user), JSON.stringify(next));
        },
        [user]
    );

    const forgetTitle = useCallback(
        (id) => {
            if (!id || !titleMapRef.current[id]) {
                return;
            }
            const next = { ...titleMapRef.current };
            delete next[id];
            titleMapRef.current = next;
            setTitleMap(next);
            localStorage.setItem(titlesKey(user), JSON.stringify(next));
        },
        [user]
    );

    const persistSessionId = useCallback(
        (id) => {
            const next = id || "";
            sessionIdRef.current = next;
            setSessionId(next);
            if (next) {
                sessionStorage.setItem(storageKey(user), next);
            } else {
                sessionStorage.removeItem(storageKey(user));
            }
        },
        [user]
    );

    const stopStream = useCallback(() => {
        streamGenRef.current += 1;
        abortRef.current?.abort();
        abortRef.current = null;
        if (coldStartRef.current) {
            clearTimeout(coldStartRef.current);
            coldStartRef.current = null;
        }
        setStreaming(false);
        setShowColdStart(false);
    }, []);

    const loadSessions = useCallback(async () => {
        try {
            const token = await getAccessToken();
            const payload = await listAgentSessions(token);
            const list = sortSessionsByUpdatedAt(normalizeSessions(payload));
            setSessions(list);
        } catch (error) {
            toast.error(error.message || t("agent.couldNotLoadChats"));
        } finally {
            setSessionsLoading(false);
        }
    }, [getAccessToken, t]);

    const loadSessionMessages = useCallback(
        async (id) => {
            if (!id) {
                setMessages([]);
                setMessagesLoading(false);
                return;
            }
            setMessagesLoading(true);
            try {
                const token = await getAccessToken();
                const session = await getAgentSession(token, id);
                setMessages(messagesFromSession(session));
                const title = firstUserMessageText(session);
                if (title) {
                    rememberTitle(id, title);
                }
            } catch (error) {
                toast.error(error.message || t("agent.couldNotLoadChat"));
                setMessages([]);
            } finally {
                setMessagesLoading(false);
            }
        },
        [getAccessToken, rememberTitle, t]
    );

    useEffect(() => {
        const map = readTitleMap(user);
        titleMapRef.current = map;
        setTitleMap(map);
        loadSessions();
    }, [loadSessions, user?.localAccountId]);

    useEffect(() => {
        const stored = sessionStorage.getItem(storageKey(user));
        if (stored) {
            persistSessionId(stored);
            loadSessionMessages(stored);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- restore once per login
    }, [user?.localAccountId]);

    useEffect(() => {
        const shell = shellRef.current;
        if (!shell) {
            return;
        }

        const apply = () => {
            const vv = window.visualViewport;
            const visible = vv ? vv.height : window.innerHeight;
            const offsetTop = vv ? vv.offsetTop : 0;
            const headerVisible = Math.max(0, HEADER_PX - offsetTop);
            shell.style.height = `${Math.max(160, Math.round(visible - headerVisible))}px`;
        };

        apply();
        window.visualViewport?.addEventListener("resize", apply);
        window.visualViewport?.addEventListener("scroll", apply);
        window.addEventListener("resize", apply);
        return () => {
            window.visualViewport?.removeEventListener("resize", apply);
            window.visualViewport?.removeEventListener("scroll", apply);
            window.removeEventListener("resize", apply);
        };
    }, []);

    const handleSelect = async (id) => {
        stopStream();
        persistSessionId(id);
        setDraft("");
        if (composerRef.current) {
            composerRef.current.style.height = "";
        }
        setListOpen(false);
        await loadSessionMessages(id);
    };

    const handleCreate = () => {
        stopStream();
        persistSessionId("");
        setMessages([]);
        setDraft("");
        if (composerRef.current) {
            composerRef.current.style.height = "";
        }
        setListOpen(false);
        if (isDesktopComposer()) {
            requestAnimationFrame(() => composerRef.current?.focus());
        }
    };

    const handleDelete = (id) => {
        setConfirmState({
            isOpen: true,
            title: t("agent.deleteThisChat"),
            confirm: async () => {
                try {
                    const token = await getAccessToken();
                    await deleteAgentSession(token, id);
                    forgetTitle(id);
                    if (id === sessionId) {
                        handleCreate();
                    }
                    await loadSessions();
                    toast.success(t("agent.chatDeleted"));
                } catch (error) {
                    toast.error(error.message || t("agent.couldNotDeleteChat"));
                }
            },
        });
    };

    const handleSend = async () => {
        const text = draft.trim();
        if (!text || streaming) {
            return;
        }

        const gen = ++streamGenRef.current;
        const activeSessionId = sessionIdRef.current;

        setDraft("");
        if (composerRef.current) {
            composerRef.current.style.height = "";
        }
        setStreaming(true);
        gotEventRef.current = false;
        setShowColdStart(false);
        if (coldStartRef.current) {
            clearTimeout(coldStartRef.current);
        }
        coldStartRef.current = setTimeout(() => {
            if (gen === streamGenRef.current && !gotEventRef.current) {
                setShowColdStart(true);
            }
        }, COLD_START_MS);

        if (activeSessionId && !titleMapRef.current[activeSessionId]) {
            rememberTitle(activeSessionId, text);
        }

        const userMessage = {
            id: `user-${Date.now()}`,
            role: "user",
            text,
            tools: [],
            artifacts: [],
        };
        const assistantMessage = {
            id: `assistant-${Date.now()}`,
            role: "assistant",
            text: "",
            tools: [],
            artifacts: [],
        };
        setMessages((prev) => [...prev, userMessage, assistantMessage]);

        const controller = new AbortController();
        abortRef.current = controller;
        const timeout = setTimeout(() => controller.abort(), STREAM_TIMEOUT_MS);

        try {
            const token = await getAccessToken();
            await streamAgentQuery({
                apiAccessToken: token,
                message: text,
                sessionId: activeSessionId,
                signal: controller.signal,
                onEvent: (rawEvent) => {
                    if (gen !== streamGenRef.current) {
                        return;
                    }
                    gotEventRef.current = true;
                    setShowColdStart(false);
                    const event = unwrapEvent(rawEvent);
                    const createdId = hubSessionIdOf(event);
                    if (createdId && createdId !== sessionIdRef.current) {
                        persistSessionId(createdId);
                        rememberTitle(createdId, text);
                    }

                    setMessages((prev) => {
                        const next = [...prev];
                        const last = next[next.length - 1];
                        if (!last || last.role !== "assistant") {
                            return prev;
                        }
                        const updated = {
                            ...last,
                            text: applyTextEvent(last.text, event),
                        };
                        mergeToolsIntoMessage(updated, event);
                        mergeArtifactsIntoMessage(updated, event);
                        if (
                            updated.text === last.text &&
                            updated.tools === last.tools &&
                            updated.artifacts === last.artifacts
                        ) {
                            return prev;
                        }
                        next[next.length - 1] = updated;
                        return next;
                    });
                },
            });
            if (gen === streamGenRef.current) {
                await loadSessions();
            }
        } catch (error) {
            if (error.name !== "AbortError" && gen === streamGenRef.current) {
                toast.error(error.message || t("agent.couldNotSend"));
            }
        } finally {
            clearTimeout(timeout);
            if (gen === streamGenRef.current) {
                if (coldStartRef.current) {
                    clearTimeout(coldStartRef.current);
                    coldStartRef.current = null;
                }
                setShowColdStart(false);
                setStreaming(false);
                abortRef.current = null;
            }
        }
    };

    const onComposerKeyDown = (event) => {
        if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) {
            return;
        }
        if (isCoarsePointer()) {
            return;
        }
        event.preventDefault();
        handleSend();
    };

    const openSessionList = () => {
        composerRef.current?.blur();
        setListOpen(true);
    };

    const activeTitle = sessionId
        ? titleMap[sessionId] || t("nav.agge")
        : t("common.newChat");

    return (
        <>
            <HeaderBar />
            <div
                ref={shellRef}
                className="flex min-h-0 flex-col overflow-hidden"
                style={{ height: "calc(100dvh - 70px)" }}
            >
                <div className="flex min-h-0 min-w-0 flex-1 flex-row overflow-hidden">
                    <Sidebar />
                    <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-mute-25">
                        <div className="flex min-h-0 flex-1 flex-col gap-0 md:gap-4 md:px-6 md:py-6">
                            <div className="hidden shrink-0 items-center justify-between gap-3 md:flex">
                                <div className="min-w-0">
                                    <h2>{t("nav.agge")}</h2>
                                    <p className="mt-1 text-sm text-mute-500">
                                        {t("agent.subtitle")}
                                    </p>
                                </div>
                            </div>
                            <div className="flex min-h-0 flex-1 gap-0 md:gap-4">
                                <AgentSessionList
                                    sessions={sessions}
                                    titleMap={titleMap}
                                    activeSessionId={sessionId}
                                    loading={sessionsLoading}
                                    onSelect={handleSelect}
                                    onCreate={handleCreate}
                                    onDelete={handleDelete}
                                    collapsed={!listOpen}
                                    onToggle={() => setListOpen(false)}
                                />
                                <section className="surface-card flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-none border-0 shadow-none md:rounded-2xl md:border md:shadow-lg">
                                    <div className="flex shrink-0 items-center gap-2 border-b border-mute-200 px-3 py-2 md:hidden">
                                        <button
                                            type="button"
                                            className="btn"
                                            onClick={openSessionList}
                                        >
                                            {t("common.chats")}
                                        </button>
                                        <p className="min-w-0 flex-1 truncate text-base font-bold text-mute-900">
                                            {activeTitle}
                                        </p>
                                    </div>
                                    <AgentMessageList
                                        messages={messages}
                                        streaming={streaming}
                                        showColdStart={showColdStart}
                                        loading={messagesLoading}
                                        sessionId={sessionId}
                                        getAccessToken={getAccessToken}
                                    />
                                    <div className="shrink-0 border-t border-mute-200 px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:p-4">
                                        <div className="flex items-end gap-2">
                                            <textarea
                                                ref={composerRef}
                                                className="max-h-32 min-h-[44px] flex-1 resize-none rounded-md border border-mute-200 bg-white px-3 py-2 text-base text-mute-900 placeholder-mute-500 focus:border-primary-500 focus:outline-none focus:ring-primary-500 md:max-h-40 md:min-h-[40px] md:px-4 md:text-sm"
                                                rows={1}
                                                placeholder={t("agent.placeholder")}
                                                value={draft}
                                                disabled={streaming}
                                                onChange={(event) => {
                                                    setDraft(event.target.value);
                                                    const el = event.target;
                                                    el.style.height = "auto";
                                                    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
                                                }}
                                                onKeyDown={onComposerKeyDown}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-primary shrink-0"
                                                disabled={streaming || !draft.trim()}
                                                onClick={handleSend}
                                            >
                                                {t("common.send")}
                                            </button>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
            <ConfirmationModal
                isOpen={confirmState.isOpen}
                title={confirmState.title}
                onClose={() => setConfirmState((state) => ({ ...state, isOpen: false }))}
                onConfirm={() => {
                    setConfirmState((state) => ({ ...state, isOpen: false }));
                    confirmState.confirm();
                }}
            />
        </>
    );
};

export default AgentPage;
